/**
 * Bittersweet Herb Farm - Global JavaScript
 * Core functionality and utilities
 */

// Initialize global namespace
window.BittersweetHerbFarm = window.BittersweetHerbFarm || {};

/**
 * Utility Functions
 */
const Utils = {
  /**
   * Debounce function calls
   */
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  },

  /**
   * Throttle function calls
   */
  throttle(func, limit) {
    let inThrottle;
    return function(...args) {
      if (!inThrottle) {
        func.apply(this, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  },

  /**
   * Format money for display
   */
  formatMoney(cents, format) {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }
    
    let value = '';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = format || '${{amount}}';

    function formatWithDelimiters(number, precision, thousands, decimal) {
      thousands = thousands || ',';
      decimal = decimal || '.';

      if (isNaN(number) || number === null) {
        return '0';
      }

      number = (number / 100.0).toFixed(precision);

      const parts = number.split('.');
      const dollarsAmount = parts[0].replace(
        /(\d)(?=(\d\d\d)+(?!\d))/g,
        '$1' + thousands
      );
      const centsAmount = parts[1] ? decimal + parts[1] : '';

      return dollarsAmount + centsAmount;
    }

    switch (formatString.match(placeholderRegex)[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'");
        break;
    }

    return formatString.replace(placeholderRegex, value);
  },

  /**
   * Fetch cart data
   */
  async getCart() {
    try {
      const response = await fetch('/cart.js');
      return await response.json();
    } catch (error) {
      console.error('Error fetching cart:', error);
      return null;
    }
  },

  /**
   * Update cart count in header
   */
  async updateCartCount() {
    const cart = await this.getCart();
    if (cart) {
      const cartCountElements = document.querySelectorAll('[data-cart-count]');
      cartCountElements.forEach(element => {
        element.textContent = cart.item_count > 99 ? '99+' : cart.item_count;
        element.setAttribute('data-count', cart.item_count);
      });
    }
  },

  /**
   * Show notification/toast message
   */
  showNotification(message, type = 'success') {
    // Prevent duplicate notifications (debounce)
    const notificationKey = `${message}-${type}`;
    const now = Date.now();
    
    // Check if this exact notification was shown in the last 500ms
    if (this._lastNotification === notificationKey && (now - this._lastNotificationTime) < 500) {
      console.log('Duplicate notification prevented:', message);
      return;
    }
    
    // Store notification info to prevent duplicates
    this._lastNotification = notificationKey;
    this._lastNotificationTime = now;
    
    // Remove any existing notifications first (only allow one at a time)
    const existingNotifications = document.querySelectorAll('.notification');
    existingNotifications.forEach(notif => {
      notif.style.animation = 'slideOutDown 0.2s ease-out';
      setTimeout(() => {
        if (notif.parentNode) {
          notif.parentNode.removeChild(notif);
        }
      }, 200);
    });
    
    // Create notification element
    const notification = document.createElement('div');
    notification.className = `notification notification--${type}`;
    notification.style.cssText = `
      position: fixed;
      bottom: var(--space-6);
      right: var(--space-6);
      background: ${type === 'success' ? 'var(--sage-700)' : '#D4183D'};
      color: white;
      padding: var(--space-4) var(--space-6);
      border-radius: var(--radius-lg);
      box-shadow: var(--shadow-xl);
      z-index: var(--z-tooltip);
      max-width: 400px;
      animation: slideInUp 0.3s ease-out;
    `;
    notification.textContent = message;
    notification.setAttribute('role', 'status');
    notification.setAttribute('aria-live', 'polite');

    // Add to page
    document.body.appendChild(notification);

    // Remove after 3 seconds
    setTimeout(() => {
      notification.style.animation = 'slideOutDown 0.3s ease-out';
      setTimeout(() => {
        if (notification.parentNode) {
          document.body.removeChild(notification);
        }
      }, 300);
    }, 3000);
  }
};

// Add animations for notifications
const style = document.createElement('style');
style.textContent = `
  @keyframes slideInUp {
    from {
      transform: translateY(100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
  
  @keyframes slideOutDown {
    from {
      transform: translateY(0);
      opacity: 1;
    }
    to {
      transform: translateY(100%);
      opacity: 0;
    }
  }
  
  @media (prefers-reduced-motion: reduce) {
    .notification {
      animation: none !important;
    }
  }
`;
document.head.appendChild(style);

/**
 * Accessibility Enhancement
 */
class AccessibilityEnhancer {
  constructor() {
    this.init();
  }

  init() {
    this.trapFocusInModals();
    this.enhanceFocusIndicators();
  }

  trapFocusInModals() {
    // Focus trap for modals and dialogs
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;

      const modal = document.querySelector('[role="dialog"][aria-hidden="false"]');
      if (!modal) return;

      const focusableElements = modal.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      
      const firstFocusable = focusableElements[0];
      const lastFocusable = focusableElements[focusableElements.length - 1];

      if (e.shiftKey && document.activeElement === firstFocusable) {
        e.preventDefault();
        lastFocusable.focus();
      } else if (!e.shiftKey && document.activeElement === lastFocusable) {
        e.preventDefault();
        firstFocusable.focus();
      }
    });
  }

  enhanceFocusIndicators() {
    // Add focus-visible class for better focus indicators
    document.addEventListener('mousedown', () => {
      document.body.classList.add('using-mouse');
    });

    document.addEventListener('keydown', () => {
      document.body.classList.remove('using-mouse');
    });
  }
}

/**
 * Scroll Animations
 */
class ScrollAnimations {
  constructor() {
    this.elements = document.querySelectorAll('[data-scroll-reveal]');
    this.init();
  }

  init() {
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              this.observer.unobserve(entry.target);
            }
          });
        },
        { 
          threshold: 0.1, 
          rootMargin: '0px 0px -50px 0px' 
        }
      );

      this.elements.forEach(el => this.observer.observe(el));
    } else {
      // Fallback for older browsers
      this.elements.forEach(el => el.classList.add('is-visible'));
    }
  }
}

/**
 * Image Lazy Loading Enhancement
 */
class LazyImageLoader {
  constructor() {
    this.images = document.querySelectorAll('img[loading="lazy"]');
    this.init();
  }

  init() {
    // Add fade-in animation when images load
    this.images.forEach(img => {
      if (img.complete) {
        img.classList.add('loaded');
      } else {
        img.addEventListener('load', () => {
          img.classList.add('loaded');
        });
      }
    });
  }
}

/**
 * USO Dynamic Price Bridge
 * Fetches per-variant discounted prices from USO and updates theme price nodes.
 */
class UsoDynamicPriceBridge {
  constructor() {
    this.endpoint = 'https://specialoffers.suppleapps.com/api/v2/page-actions?data-product-handles';
    this.cache = new Map();
    this.inFlight = new Map();
  }

  init() {
    if (!window.saso || !window.saso.shop_slug) return;
    this.refreshAll();

    document.addEventListener('shopify:section:load', () => this.refreshAll());
    document.addEventListener('variant:change', (event) => this.handleVariantChange(event));
    document.addEventListener('cart:updated', () => {
      // Cart contents drive the qualifying quantity, so every cached price is stale.
      this.cache.clear();
      this.refreshAll();
    });
  }

  refreshAll(root = document) {
    const targets = Array.from(root.querySelectorAll('[data-uso-price-target]'));

    // Cart lines are priced together (see updateCartTargets); everything else —
    // PDP, sticky bar, product cards — is a standalone "what would this cost" preview.
    const cartTargets = targets.filter((target) => target.dataset.usoContext === 'cart-item');
    const singleTargets = targets.filter((target) => target.dataset.usoContext !== 'cart-item');

    if (cartTargets.length) this.updateCartTargets(cartTargets);
    singleTargets.forEach((target) => this.updateTarget(target));
  }

  handleVariantChange(event) {
    const variant = event && event.detail && event.detail.variant ? event.detail.variant : null;
    if (!variant) return;

    const targets = document.querySelectorAll(
      '[data-uso-price-target][data-uso-product-id]:not([data-uso-context="cart-item"])'
    );
    targets.forEach((target) => {
      target.dataset.usoVariantId = String(variant.id);
      target.dataset.usoPriceCents = String(variant.price);
      target.dataset.usoRequiresShipping = variant.requires_shipping ? 'true' : 'false';
      target.dataset.usoVariantTitle = variant.title || '';
      this.updateTarget(target);
    });
  }

  async updateTarget(target) {
    const payloadData = this.readTargetData(target);
    if (!payloadData) return;

    const cacheKey = [
      payloadData.variantId,
      payloadData.priceCents,
      payloadData.collectionIds.join(','),
      payloadData.requiresShipping ? 1 : 0,
    ].join(':');

    let result;
    if (this.cache.has(cacheKey)) {
      result = this.cache.get(cacheKey);
    } else if (this.inFlight.has(cacheKey)) {
      result = await this.inFlight.get(cacheKey);
    } else {
      const requestPromise = this.fetchDiscountedPrice(payloadData)
        .then((resolved) => {
          this.cache.set(cacheKey, resolved);
          this.inFlight.delete(cacheKey);
          return resolved;
        })
        .catch((error) => {
          this.inFlight.delete(cacheKey);
          console.warn('[USO] Price fetch failed for variant', payloadData.variantId, error);
          return null;
        });

      this.inFlight.set(cacheKey, requestPromise);
      result = await requestPromise;
    }

    this.applyResult(target, result);
  }

  readTargetData(target) {
    const variantId = Number(target.dataset.usoVariantId);
    const productId = Number(target.dataset.usoProductId);
    const priceCents = Number(target.dataset.usoPriceCents);
    const quantity = Math.max(1, Number(target.dataset.usoQuantity || 1));
    const collectionIds = (target.dataset.usoCollections || '')
      .split(',')
      .map((value) => Number(value.trim()))
      .filter((value) => Number.isFinite(value));

    if (!variantId || !productId || !Number.isFinite(priceCents)) return null;

    return {
      variantId,
      productId,
      priceCents,
      lineKey: target.dataset.usoLineKey || '',
      handle: target.dataset.usoHandle || '',
      productTitle: target.dataset.usoProductTitle || '',
      variantTitle: target.dataset.usoVariantTitle || '',
      requiresShipping: target.dataset.usoRequiresShipping === 'true',
      quantity,
      collectionIds,
    };
  }

  /**
   * Cart page: price the ENTIRE cart in one request, then map each result back to its line.
   *
   * A USO volume offer can be configured "per entire product selection", where the
   * qualifying quantity is the SUM of every eligible line in the cart — not the quantity
   * of any one line. Pricing each line against a synthetic single-line cart therefore
   * hides the discount until one line clears the tier on its own, so the cart under-reports
   * what the customer is actually charged at checkout. Sending the real cart is what makes
   * the line items agree with checkout.
   */
  async updateCartTargets(targets) {
    const lines = targets
      .map((target) => ({ target, data: this.readTargetData(target) }))
      .filter((line) => line.data);

    if (!lines.length) {
      targets.forEach((target) => this.applyResult(target, null));
      return;
    }

    const cacheKey = `cart:${lines
      .map(({ data }, index) => `${data.lineKey || `${data.variantId}#${index}`}x${data.quantity}@${data.priceCents}`)
      .join('|')}`;

    let results;
    if (this.cache.has(cacheKey)) {
      results = this.cache.get(cacheKey);
    } else if (this.inFlight.has(cacheKey)) {
      results = await this.inFlight.get(cacheKey);
    } else {
      const requestPromise = this.fetchCartPrices(lines.map((line) => line.data))
        .then((resolved) => {
          this.cache.set(cacheKey, resolved);
          this.inFlight.delete(cacheKey);
          return resolved;
        })
        .catch((error) => {
          this.inFlight.delete(cacheKey);
          console.warn('[USO] Cart price fetch failed', error);
          return null;
        });

      this.inFlight.set(cacheKey, requestPromise);
      results = await requestPromise;
    }

    lines.forEach(({ target, data }, index) => {
      const key = this.lineKeyFor(data, index);
      this.applyResult(target, results ? results.get(key) || null : null);
    });
  }

  lineKeyFor(data, index) {
    // Real Shopify line keys disambiguate the same variant appearing on multiple lines
    // (different properties or selling plans); fall back to index for preview contexts.
    return data.lineKey || `${data.variantId}:uso-preview-${index}`;
  }

  buildCartPayload(dataList) {
    const items = dataList.map((data, index) => {
      const lineTotal = data.priceCents * data.quantity;
      return {
        id: data.variantId,
        key: this.lineKeyFor(data, index),
        variant_id: data.variantId,
        product_id: data.productId,
        handle: data.handle,
        quantity: data.quantity,
        price: data.priceCents,
        original_price: data.priceCents,
        discounted_price: data.priceCents,
        final_price: data.priceCents,
        line_price: lineTotal,
        original_line_price: lineTotal,
        discounted_line_price: lineTotal,
        final_line_price: lineTotal,
        product_title: data.productTitle,
        variant_title: data.variantTitle,
        options_with_values: [],
        upsell_notes: [],
        discount_notes: [],
        discounts: [],
      };
    });

    const cartCollections = {};
    dataList.forEach((data) => {
      cartCollections[`v${data.variantId}`] = data.collectionIds;
    });

    const subtotal = items.reduce((sum, item) => sum + item.line_price, 0);
    const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

    return {
      shop_slug: window.saso.shop_slug,
      money_format: window.saso.money_format || '${{amount}}',
      customer: window.saso.customer || null,
      cart_path: window.saso.cart_path || '/cart',
      cartDiscountRenderMethod: window.saso.cartDiscountRenderMethod || 'SECTION',
      page_type: 'cart',
      cart_collections: cartCollections,
      cart: {
        token: 'uso-preview',
        note: null,
        attributes: {},
        original_total_price: subtotal,
        total_price: subtotal,
        total_discount: 0,
        total_weight: 0,
        item_count: itemCount,
        items_subtotal_price: subtotal,
        cart_level_discount_applications: [],
        requires_shipping: dataList.some((data) => data.requiresShipping),
        currency: (window.saso.cart && window.saso.cart.currency) || 'USD',
        items,
      },
    };
  }

  async fetchCartPrices(dataList) {
    const payload = this.buildCartPayload(dataList);

    const response = await fetch(this.endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!response.ok) return null;

    const result = await response.json();
    const discounts = result && typeof result.discounts === 'object' ? result.discounts : null;
    const cartData = discounts && discounts.cart && Array.isArray(discounts.cart.items) ? discounts.cart : null;
    if (!cartData) return null;

    const sentItems = payload.cart.items;
    const byLineKey = new Map();

    cartData.items.forEach((discountItem, index) => {
      const sentItem = sentItems.find((item) => item.key === discountItem.key) || sentItems[index];
      if (!sentItem) return;

      const parsed = this.parseDiscountItem(discountItem, sentItem);
      if (parsed) byLineKey.set(sentItem.key, parsed);
    });

    return byLineKey;
  }

  async fetchDiscountedPrice(data) {
    const results = await this.fetchCartPrices([data]);
    if (!results) return null;
    return results.get(this.lineKeyFor(data, 0)) || null;
  }

  parseDiscountItem(discountItem, sentItem) {
    const originalRaw = discountItem.original_price;
    const discountedRaw = discountItem.discounted_price;

    if (typeof originalRaw !== 'number' || typeof discountedRaw !== 'number') return null;
    if (!(discountedRaw < originalRaw)) return null;

    // The API answers in store currency units (15) while the theme works in cents (1500).
    // Resolve the scale by finding which one reproduces the price we sent — that doubles as
    // a sanity check: the backend re-prices some lines from its own catalog and ignores what
    // we send (a free-gift line the app already zeroed comes back at full catalog price).
    // If the basis it quotes isn't the price on the line, we must not paint it over the
    // real one — leave the Liquid-rendered price alone.
    const scale = [100, 1].find((factor) => Math.round(originalRaw * factor) === sentItem.original_price);
    if (!scale) return null;

    const savingsPerUnitCents = Math.round((originalRaw - discountedRaw) * scale);

    return {
      discountedPriceFormatted: discountItem.discounted_price_format || Utils.formatMoney(sentItem.original_price),
      originalPriceFormatted: discountItem.original_price_format || Utils.formatMoney(sentItem.original_price),
      discountedLinePriceFormatted: discountItem.discounted_line_price_format || discountItem.discounted_price_format || Utils.formatMoney(sentItem.line_price),
      originalLinePriceFormatted: discountItem.original_line_price_format || discountItem.original_price_format || Utils.formatMoney(sentItem.line_price),
      discountedPriceRaw: discountedRaw,
      originalPriceRaw: originalRaw,
      lineSavingsCents: savingsPerUnitCents * sentItem.quantity,
    };
  }

  applyResult(target, result) {
    const currentPriceElement = target.querySelector('[data-uso-price-current]');
    const comparePriceElement = target.querySelector('[data-uso-price-compare]');
    const currentLinePriceElement = target.querySelector('[data-uso-price-current-line]');
    const compareLinePriceElement = target.querySelector('[data-uso-price-compare-line]');
    const currentEachPriceElement = target.querySelector('[data-uso-price-current-each]');
    const compareEachPriceElement = target.querySelector('[data-uso-price-compare-each]');
    const savingsElement = target.querySelector('[data-uso-price-savings]');
    if (!currentPriceElement && !currentLinePriceElement && !currentEachPriceElement) return;

    this.captureBaseValue(currentPriceElement);
    this.captureBaseValue(comparePriceElement);
    this.captureBaseValue(currentLinePriceElement);
    this.captureBaseValue(compareLinePriceElement);
    this.captureBaseValue(currentEachPriceElement);
    this.captureBaseValue(compareEachPriceElement);
    this.captureBaseValue(savingsElement);

    if (result && result.discountedPriceFormatted) {
      if (currentPriceElement) {
        currentPriceElement.textContent = result.discountedPriceFormatted;
      }
      if (currentLinePriceElement) {
        currentLinePriceElement.textContent = result.discountedLinePriceFormatted || result.discountedPriceFormatted;
      }
      if (currentEachPriceElement) {
        currentEachPriceElement.textContent = result.discountedPriceFormatted;
      }

      if (comparePriceElement) {
        comparePriceElement.textContent = result.originalPriceFormatted;
        comparePriceElement.style.display = 'inline-flex';
      }
      if (compareLinePriceElement) {
        compareLinePriceElement.textContent = result.originalLinePriceFormatted || result.originalPriceFormatted;
        compareLinePriceElement.style.display = 'inline-flex';
      }
      if (compareEachPriceElement) {
        compareEachPriceElement.textContent = result.originalPriceFormatted;
        compareEachPriceElement.style.display = 'inline-flex';
      }
      if (savingsElement && result.originalPriceRaw > result.discountedPriceRaw) {
        const savingsPercent = Math.round(((result.originalPriceRaw - result.discountedPriceRaw) / result.originalPriceRaw) * 100);
        const savingsCents = result.lineSavingsCents;
        savingsElement.textContent = savingsCents > 0
          ? `Save ${savingsPercent}% · ${Utils.formatMoney(savingsCents)}`
          : `Save ${savingsPercent}%`;
        savingsElement.style.display = 'inline-flex';
      }
      target.setAttribute('data-uso-price-applied', 'true');
    } else {
      this.restoreBaseValue(currentPriceElement);
      this.restoreBaseValue(comparePriceElement);
      this.restoreBaseValue(currentLinePriceElement);
      this.restoreBaseValue(compareLinePriceElement);
      this.restoreBaseValue(currentEachPriceElement);
      this.restoreBaseValue(compareEachPriceElement);
      this.restoreBaseValue(savingsElement);
      target.removeAttribute('data-uso-price-applied');
    }
  }

  captureBaseValue(element) {
    if (!element || element.hasAttribute('data-uso-base-captured')) return;
    element.setAttribute('data-uso-base-captured', 'true');
    element.setAttribute('data-uso-base-text', element.textContent || '');
    element.setAttribute('data-uso-base-display', element.style.display || '');
  }

  restoreBaseValue(element) {
    if (!element || !element.hasAttribute('data-uso-base-captured')) return;
    element.textContent = element.getAttribute('data-uso-base-text') || '';
    element.style.display = element.getAttribute('data-uso-base-display') || '';
  }
}

/**
 * Initialize on DOM ready
 */
document.addEventListener('DOMContentLoaded', () => {
  // Initialize utilities
  window.BittersweetHerbFarm.utils = Utils;
  
  // Initialize features
  new AccessibilityEnhancer();
  new ScrollAnimations();
  new LazyImageLoader();
  const usoPriceBridge = new UsoDynamicPriceBridge();
  usoPriceBridge.init();
  window.BittersweetHerbFarm.usoPriceBridge = usoPriceBridge;
  
  // Update cart count on page load
  Utils.updateCartCount();
  
  // Listen for cart updates
  document.addEventListener('cart:updated', () => {
    Utils.updateCartCount();
  });
});

// Export for use in other scripts
window.BittersweetHerbFarm.Utils = Utils;
